import { TItemType, TResolvedItemsMap } from '@modules/items';
import {
  IBaseDetectedElement,
  IItemDefinition,
  IBaseDetectionRun,
  TPollDetectionReturn,
  IItemDefinitionDto,
} from './shared.types';
import { SubsetOf } from '@shared/types';

// --- DATA SCHEMA ---

export type TReferenceDetectionItemType = SubsetOf<TItemType, 'DOCUMENT' | 'FILE'>;

export interface IDetectedReference extends IBaseDetectedElement {
  /** IDs of candidate target items */
  targetItems: IItemDefinition<TReferenceDetectionItemType>[];

  /** Item ID selected by the user for insertion (optional) */
  chosenTargetItem?: IItemDefinition<TReferenceDetectionItemType>;
}

export interface IDetectedSelfReference extends IBaseDetectedElement {
  headingId: string;
  isChosen: boolean;
}

/**
 * Core model for a reference detection run.
 */
export interface IReferenceDetectionRun extends IBaseDetectionRun {
  selectedItems: IItemDefinition<TReferenceDetectionItemType>[];

  /** List of all detected references */
  detectedReferences: IDetectedReference[];

  /** List of detected self-references within the same document */
  detectedSelfReferences: IDetectedSelfReference[];
}

export const isIDetectedSelfReference = (
  ref: IDetectedReference | IDetectedSelfReference
): ref is IDetectedSelfReference => {
  return Boolean((ref as IDetectedSelfReference).headingId);
};

// --- ENDPOINT DATA TYPES ---

export type TReferenceDetectionSelectableItemType = TReferenceDetectionItemType | 'FOLDER';
/** Payload DTO for starting a reference detection run. */
export type TCreateReferenceDetectionDto = {
  /** ID of the root item (folder) */
  rootItemId: string;
  /** IDs of items (docs/files/folders) selected for detection */
  selectedItems: IItemDefinitionDto<TReferenceDetectionSelectableItemType>[];
};

/** DTO representing a user-chosen reference for insertion. */
export type TChosenReferenceDto = {
  /** Detected reference ID */
  id: string;
  /** Chosen target item (if not provided the first option will be used) */
  chosenTargetItem?: IItemDefinitionDto<TReferenceDetectionItemType>;
};

/** Payload DTO for submitting a reference detection run. */
export interface ISubmitReferenceDetectionDto {
  /** Array of selected references with target docs */
  chosenReferences: TChosenReferenceDto[];
  /** Array of selected self-reference ids */
  chosenSelfReferences: string[];
}

export enum EDetectionRefVariant {
  EXTERNAL = 'EXTERNAL',
  SELF = 'SELF',
}
